import React, { Component } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { decorators, Paginator } from "@orange-marmalade/paginate-this";
import { INCOMING_LINE_ITEMS } from "MODULES/IncomingQuotes/constants";
import { NoteTooltip } from "MODULES/shared/components/NoteTooltip";
import { DataTable, PartNumber, IconReceived } from "MODULES/shared/components";
import { dollars } from "LIB/formatters/currency";
import { AsIsFlag } from "MODULES/IncomingQuotes/shared/AsIsFlag";
import { alphaMonthYear } from "LIB/formatters/date";
import { displayCost } from "MODULES/PurchaseOrders/shared/helpers";
import { isEmpty } from "lodash";
import { LEAD_TIME_LABEL } from "MODULES/inventories/constants";
import { LineItemControls } from "MODULES/Lots/Detail/LineItemsTable/LineItemControls";
import { translateProps } from "MODULES/shared/decorators";
import EditPart from "./EditPart/EditPart";

const paginatorList = "lots/lineItems";
const ReceivedIcon = ({ quantity_received: quantityReceived, quantity }) => {
  if (quantityReceived === quantity) {
    return <IconReceived />;
  }
  return null;
};

ReceivedIcon.propTypes = {
  quantity_received: PropTypes.number,
  quantity: PropTypes.number
};

class LineItemsTable extends Component {
  static propTypes = {
    results: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
    isLoading: PropTypes.bool.isRequired
  };

  state = {
    addPartModalOpen: false,
    editPartModalOpen: false
  };

  openEditPartModal = lineItem =>
    this.setState({
      editPartModalOpen: true,
      selectedLineItem: lineItem
    });

  closeEditPartModal = () => this.setState({ editPartModalOpen: false });

  profit = lineItem =>
    Number(lineItem.total_sales) -
    (Number(lineItem.cost) + Number(lineItem.total_cost_adjustments)) *
      Number(lineItem.quantity);

  renderRow = (r, idx) => (
    <tr
      key={r.id}
      className={classNames("clickable", { "text-muted": r.cancelled })}
      onClick={() => !r.cancelled && this.openEditPartModal(r)}
    >
      <td>
        <div style={{ whiteSpace: "nowrap" }}>
          <div style={{ textAlign: "right" }}>{idx + 1}</div>
        </div>
      </td>
      <td>
        <PartNumber part={r.part} />
      </td>
      <td>
        {r.serial_number === "Various" ||
        !r.inventory_link_id ||
        (!r.serial_number && !isEmpty(r.expected_serial_number)) ? (
          r.serial_number || r.expected_serial_number
        ) : (
          <Link to={`/inventories/${r.inventory_link_id}`}>
            {r.serial_number}
          </Link>
        )}
      </td>
      <td>
        {r.alternate &&
          (r.alternate.alternate_part_id ? (
            <PartNumber
              part={{
                id: r.alternate.alternate_part_id,
                part_number: r.alternate.name
              }}
            />
          ) : (
            r.alternate.name
          ))}
      </td>
      <td>
        {r.trace_type && INCOMING_LINE_ITEMS.traceTypes[r.trace_type].label}
      </td>
      <td>{r.traced_to}</td>
      <td>{alphaMonthYear(r.tag_date)}</td>
      <td>{r.tagged_by}</td>
      <td className="text-center">{r.lead_time}</td>
      <td>
        {r.condition && INCOMING_LINE_ITEMS.condition[r.condition].label}
        <AsIsFlag lineItem={r} className="m-l-5" />
      </td>
      <td className="text-right">{r.quantity}</td>
      <td className="text-right">
        <ReceivedIcon {...r} /> {r.quantity_received}
      </td>
      <td className="text-right">
        {displayCost(r) && dollars(displayCost(r) * Number(r.quantity))}
      </td>
      <td className="text-right">
        {displayCost(r) &&
          dollars(
            (Number(displayCost(r)) + Number(r.total_cost_adjustments)) *
              Number(r.quantity)
          )}
      </td>
      <td className="text-right">
        {Number(r.total_sales) !== 0 && dollars(r.total_sales)}
      </td>
      <td className="text-right">
        {Number(this.profit(r)) !== 0 &&
          Number(r.total_sales) !== 0 &&
          dollars(this.profit(r))}
      </td>
      <td className="text-center td-shrink-icon">
        {(r.internal_note || r.external_note) && (
          <NoteTooltip
            note={[r.internal_note, r.external_note]
              .filter(n => n !== null && n !== "")
              .join(" | ")}
            className={classNames({ "link-muted": r.cancelled })}
          />
        )}
      </td>
      <td className="text-center td-shrink-icon">
        <LineItemControls lineItem={r} />
      </td>
    </tr>
  );

  render() {
    const { lot, results, isLoading } = this.props;
    const { editPartModalOpen, selectedLineItem } = this.state;

    return (
      <div>
        <EditPart
          open={editPartModalOpen}
          close={this.closeEditPartModal}
          selectedLineItem={selectedLineItem}
        />
        <DataTable.Mini loading={isLoading} numbered noMargin>
          <DataTable.Head>
            <th />
            <th>Part #</th>
            <th>Serial #</th>
            <th>Buy As Alt</th>
            <th>Exp Trace Type</th>
            <th>Exp Trace To</th>
            <th>Tag Date</th>
            <th>Tagged By</th>
            <th>{LEAD_TIME_LABEL}</th>
            <th>Condition</th>
            <th className="text-right">Qty Ord.</th>
            <th className="text-right">Qty Rec.</th>
            <th className="text-right">Applied Cost</th>
            <th className="text-right">Total Cost</th>
            <th className="text-right">Total Sales</th>
            <th className="text-right">Profit</th>
            <th className="text-center" />
            <th className="text-center" />
          </DataTable.Head>
          <DataTable.Body>
            {results.map(this.renderRow)}
            <tr style={{ backgroundColor: "transparent" }}>
              <td />
              <td className="text-right" colSpan={12}>
                <h4 className="margin-top-none margin-bottom-none">
                  Total: {dollars(lot.applied_cost)}
                </h4>
              </td>
            </tr>
          </DataTable.Body>
        </DataTable.Mini>
        <nav
          className="text-center margin-top-none"
          aria-label="Search results pages"
        >
          <Paginator listId={paginatorList} />
        </nav>
      </div>
    );
  }
}

const withListId = translateProps(() => ({
  listId: "lots/lineItems"
}));

export default withListId(decorators.tabulate(LineItemsTable));



// WEBPACK FOOTER //
// ./app/modules/Lots/Detail/LineItemsTable/LineItemsTable.jsx